import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CancelOpticalOrderRequest,
  CreateOpticalLabInput,
  OpticalJobCollection,
  OpticalJobDetail,
  OpticalJobListItem,
  OpticalJobListQuery,
  OpticalLabSummary,
  OpticalLabTicket,
  OpticalOrderCancellation,
  OpticalPrescriptionInput,
  PaginatedResult,
  UpdateOpticalJobInput,
  UpdateOpticalLabInput,
} from '@optiflow/types';
import { salesKeys } from '@/modules/sales/api/sales.api';
import { api } from '@/services/apiClient';
import { useIdempotentMutation } from '@/services/useIdempotentMutation';
import { useAuthStore } from '@/store/authStore';
import { opticalOrderKeys } from './opticalOrders.api';

/** Tracking keys. A list is keyed by the branch it was read in, since the queue is that branch's. */
export const opticalJobKeys = {
  all: ['optical-jobs'] as const,
  lists: () => [...opticalJobKeys.all, 'list'] as const,
  queue: (branchId: string | null, query: OpticalJobListQuery) =>
    [...opticalJobKeys.lists(), 'queue', branchId, query] as const,
  customer: (customerId: string, query: OpticalJobListQuery) =>
    [...opticalJobKeys.lists(), 'customer', customerId, query] as const,
  detail: (jobId: string) => [...opticalJobKeys.all, 'detail', jobId] as const,
  collection: (jobId: string) => [...opticalJobKeys.all, 'collection', jobId] as const,
  ticket: (jobId: string) => [...opticalJobKeys.all, 'ticket', jobId] as const,
  labs: (includeInactive: boolean) => [...opticalJobKeys.all, 'labs', includeInactive] as const,
};

/** Only the filters actually set travel, and never a prescription value: there is none to send. */
function listParams(query: OpticalJobListQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (query.page) params.page = query.page;
  if (query.pageSize) params.pageSize = query.pageSize;
  if (query.search?.trim()) params.search = query.search.trim();
  if (query.status) params.status = query.status;
  if (query.open) params.open = true;
  if (query.labId) params.labId = query.labId;
  return params;
}

/** The active branch's queue. Nothing is asked without a branch: the screen asks the user to choose one first. */
export function useOpticalJobQueueQuery(query: OpticalJobListQuery) {
  const branchId = useAuthStore((state) => state.activeBranchId);
  return useQuery({
    queryKey: opticalJobKeys.queue(branchId, query),
    queryFn: () =>
      api.get<PaginatedResult<OpticalJobListItem>>('/optical/jobs', {
        params: listParams(query),
      }),
    enabled: Boolean(branchId),
    placeholderData: keepPreviousData,
  });
}

/** One customer's jobs in the branches the user can reach. */
export function useCustomerOpticalJobsQuery(customerId: string, query: OpticalJobListQuery) {
  return useQuery({
    queryKey: opticalJobKeys.customer(customerId, query),
    queryFn: () =>
      api.get<PaginatedResult<OpticalJobListItem>>(`/optical/customers/${customerId}/jobs`, {
        params: listParams(query),
      }),
    enabled: Boolean(customerId),
    placeholderData: keepPreviousData,
  });
}

/**
 * One job, prescription included. Read when asked, not whenever the window regains focus: each read of a clinical snapshot is audited, and nothing on this screen changes behind the user's back.
 *
 * Dropped from the cache as soon as the screen lets go of it, so prescription values do not linger in memory.
 */
export function useOpticalJobQuery(jobId: string) {
  return useQuery({
    queryKey: opticalJobKeys.detail(jobId),
    queryFn: () => api.get<OpticalJobDetail>(`/optical/jobs/${jobId}`),
    enabled: Boolean(jobId),
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
}

/** What handing the pair over waits on. Needs sales.view; the caller asks only when the user holds it. */
export function useOpticalJobCollectionQuery(jobId: string, enabled: boolean) {
  return useQuery({
    queryKey: opticalJobKeys.collection(jobId),
    queryFn: () => api.get<OpticalJobCollection>(`/optical/jobs/${jobId}/collection`),
    enabled: enabled && Boolean(jobId),
  });
}

/** The lab ticket: read once for the page that prints it, never kept afterwards. */
export function useLabTicketQuery(jobId: string) {
  return useQuery({
    queryKey: opticalJobKeys.ticket(jobId),
    queryFn: () => api.get<OpticalLabTicket>(`/optical/jobs/${jobId}/lab-ticket`),
    enabled: Boolean(jobId),
    refetchOnWindowFocus: false,
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
  });
}

/** After a job changes: the job itself is replaced by the server's answer, and everything that summarises it is read again. */
function useJobChanged(jobId: string) {
  const queryClient = useQueryClient();
  return (job: OpticalJobDetail) => {
    queryClient.setQueryData(opticalJobKeys.detail(jobId), job);
    void queryClient.invalidateQueries({ queryKey: opticalJobKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: opticalJobKeys.collection(jobId) });
    void queryClient.invalidateQueries({ queryKey: salesKeys.all });
    void queryClient.invalidateQueries({ queryKey: opticalOrderKeys.orderJobs(job.orderId) });
  };
}

/** The forward steps of the lab workflow and the handover. Each carries the version the screen showed; a stale one is refused and never retried. */
export type OpticalJobAction =
  'send-to-lab' | 'mark-in-lab' | 'receive-from-lab' | 'mark-ready' | 'deliver';

export interface OpticalJobActionBody {
  version: number;
  labId?: string;
  labReference?: string;
}

export function useOpticalJobActionMutation(jobId: string) {
  const changed = useJobChanged(jobId);
  return useMutation({
    mutationFn: ({ action, body }: { action: OpticalJobAction; body: OpticalJobActionBody }) =>
      api.post<OpticalJobDetail>(`/optical/jobs/${jobId}/${action}`, body),
    onSuccess: changed,
  });
}

/** Lab, lab notes and fitting heights of a job still ORDERED. */
export function useUpdateOpticalJobMutation(jobId: string) {
  const changed = useJobChanged(jobId);
  return useMutation({
    mutationFn: (body: UpdateOpticalJobInput) =>
      api.patch<OpticalJobDetail>(`/optical/jobs/${jobId}`, body),
    onSuccess: changed,
  });
}

/** Replaces the whole prescription of a job still ORDERED: a clinical one by its id alone, or an external one typed in full. */
export function useReplaceJobPrescriptionMutation(jobId: string) {
  const changed = useJobChanged(jobId);
  return useMutation({
    mutationFn: (body: { version: number; prescription: OpticalPrescriptionInput }) =>
      api.put<OpticalJobDetail>(`/optical/jobs/${jobId}/prescription`, body),
    onSuccess: changed,
  });
}

/**
 * Cancels the whole order before the lab. One key per intent: the same reason and references sent again after a lost answer replay the recorded result instead of cancelling twice.
 *
 * The reason and any refund reference travel in the body only — never an address, a log or a stored draft.
 */
export function useCancelOpticalOrderMutation(orderId: string) {
  const queryClient = useQueryClient();
  return useIdempotentMutation<OpticalOrderCancellation, CancelOpticalOrderRequest>(
    (body, config) =>
      api.post<OpticalOrderCancellation>(`/sales/optical/orders/${orderId}/cancel`, body, config),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: opticalJobKeys.all });
        void queryClient.invalidateQueries({ queryKey: salesKeys.all });
        void queryClient.invalidateQueries({ queryKey: opticalOrderKeys.orderJobs(orderId) });
      },
    },
  );
}

/** Every lab, the inactive ones too when managing them. */
export function useOpticalLabsListQuery(includeInactive: boolean) {
  return useQuery({
    queryKey: opticalJobKeys.labs(includeInactive),
    queryFn: () =>
      api.get<OpticalLabSummary[]>('/optical/labs', {
        params: includeInactive ? { includeInactive: true } : {},
      }),
  });
}

function useLabsChanged() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [...opticalJobKeys.all, 'labs'] });
    void queryClient.invalidateQueries({ queryKey: opticalOrderKeys.labs() });
  };
}

export function useCreateOpticalLabMutation() {
  const changed = useLabsChanged();
  return useMutation({
    mutationFn: (body: CreateOpticalLabInput) => api.post<OpticalLabSummary>('/optical/labs', body),
    onSuccess: changed,
  });
}

export function useUpdateOpticalLabMutation() {
  const changed = useLabsChanged();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOpticalLabInput }) =>
      api.patch<OpticalLabSummary>(`/optical/labs/${id}`, body),
    onSuccess: changed,
  });
}
